import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useVenues } from '../contexts/VenueContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Logo from '../components/Logo';
import { IASI_POIS, getDistanceLabel } from '../config/locationsIasi';

// Leaflet Imports
import { MapContainer, TileLayer, Marker, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';

const { FiMapPin, FiNavigation, FiSearch, FiArrowLeft, FiAlertTriangle } = FiIcons;

// Haversine formula pentru calcularea distanței în metri
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 999999;
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c * 1000;
};

// Componentă pentru a muta harta pe noile coordonate
const ChangeView = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => { map.flyTo([center.lat, center.lng], zoom); }, [center, zoom, map]);
  return null;
};

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { venues } = useVenues();

  const [locationQuery, setLocationQuery] = useState(searchParams.get('q') || 'Iași');
  const [searchCenter, setSearchCenter] = useState({ lat: 47.1585, lng: 27.6014 });
  const [activePoi, setActivePoi] = useState(null);

  const [budget, setBudget] = useState(500);
  const [radius, setRadius] = useState(2500); // 2.5km default rază
  const [filteredVenues, setFilteredVenues] = useState([]);

  useEffect(() => {
    const query = locationQuery.toLowerCase();
    let foundPoi = null;
    
    // 1. Detectăm dacă query-ul contine un POI cunoscut (ex: Palas)
    for (const [key, data] of Object.entries(IASI_POIS)) {
      if (query.includes(key)) {
        foundPoi = { ...data, name: key };
        break;
      }
    }

    if (foundPoi) {
      setSearchCenter({ lat: foundPoi.lat, lng: foundPoi.lng });
      setActivePoi(foundPoi);
    } else {
      setActivePoi(null);
    }

    // 2. Filtrare semantică și calcul distanță
    const results = venues.map(venue => {
      const dist = calculateDistance(
        foundPoi?.lat || searchCenter.lat,
        foundPoi?.lng || searchCenter.lng,
        venue.lat,
        venue.lng
      );
      return { ...venue, distance: dist };
    }).filter(venue => {
      const matchBasic = venue.name.toLowerCase().includes(query) || 
                         (venue.address && venue.address.toLowerCase().includes(query));
      
      const matchLandmarks = venue.nearby_landmarks?.some(landmark => 
        landmark.name.toLowerCase().includes(query)
      );

      const matchFacilities = venue.facilities?.some(f => f.toLowerCase().includes(query));

      const matchDistance = venue.distance <= radius;
      const matchBudget = venue.price_per_night <= budget;

      // Un imobil intră în rezultate dacă are un match de text SAU dacă este pur și simplu în raza hărții căutate
      return (matchBasic || matchLandmarks || matchFacilities || matchDistance) && matchBudget;
    });

    // 3. Sortare inteligentă
    const sortedResults = [...results].sort((a, b) => {
      // Reperele match-uite explicit au prioritate
      const aHasLandmark = a.nearby_landmarks?.some(l => l.name.toLowerCase().includes(query));
      const bHasLandmark = b.nearby_landmarks?.some(l => l.name.toLowerCase().includes(query));
      
      if (aHasLandmark && !bHasLandmark) return -1;
      if (!aHasLandmark && bHasLandmark) return 1;
      
      // Dacă nu, se sortează după distanța față de centrul de pe hartă
      return (a.distance || 0) - (b.distance || 0);
    });

    setFilteredVenues(sortedResults);
  }, [locationQuery, venues, budget, radius, searchCenter.lat, searchCenter.lng]);

  const createPriceIcon = (price) => {
    return L.divIcon({
      className: 'bg-transparent',
      html: `<div class="bg-blue-600 text-white font-bold text-xs px-2 py-1 rounded-lg shadow-lg border border-white">€${price}</div>`,
      iconSize: [40, 25]
    });
  };

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      {/* Search Header */}
      <div className="p-4 border-b flex flex-col lg:flex-row gap-4 items-center bg-gray-50">
        <div onClick={() => navigate('/')} className="cursor-pointer">
          <Logo iconClassName="h-6 w-6" textClassName="text-lg" />
        </div>
        
        <div className="flex-1 flex gap-2 w-full">
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-gray-400" />
            <input 
              type="text" 
              value={locationQuery}
              onChange={(e) => setLocationQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Palas, Copou, Aeroport, Arena Tenis..."
            />
          </div>
          <div className="flex items-center gap-4 bg-white px-4 py-2 rounded-xl border border-gray-200">
             <span className="text-xs font-bold text-gray-500 uppercase">Rază: {(radius/1000).toFixed(1)}km</span>
             <input type="range" min="500" max="10000" step="500" value={radius} onChange={e => setRadius(parseInt(e.target.value))} className="accent-blue-600" />
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* LIST SECTION */}
        <div className="w-full lg:w-1/2 overflow-y-auto p-4 lg:p-6 bg-gray-50 order-2 lg:order-1">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              {filteredVenues.length} rezultate {activePoi ? `în zona ${activePoi.label}` : 'în Iași'}
            </h2>

            <div className="space-y-6">
              {filteredVenues.map(venue => (
                <motion.div 
                  key={venue.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl shadow-sm border overflow-hidden flex flex-col sm:flex-row cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/book/${venue.owner_id}/${venue.id}`)}
                >
                  <div className="w-full sm:w-48 h-48 sm:h-auto shrink-0 relative">
                    <img src={venue.photos?.[0]} className="w-full h-full object-cover" alt={venue.name} />
                    {venue.is_promoted && (
                      <div className="absolute top-2 left-2 bg-yellow-400 text-yellow-900 text-[10px] font-bold px-2 py-1 rounded">VIP</div>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start">
                        <h3 className="font-bold text-gray-900 leading-tight">{venue.name}</h3>
                        <span className="text-xl font-black text-blue-600">€{venue.price_per_night}</span>
                      </div>
                      
                      <div className="mt-2 flex flex-col gap-1">
                        <div className="flex items-center gap-1 text-gray-500 text-xs">
                          <SafeIcon icon={FiMapPin} className="h-3 w-3" />
                          <span>{venue.address}</span>
                        </div>
                        
                        {/* Distanta fața de POI-ul central (Ex: Palas) */}
                        {(venue.distance !== undefined && activePoi) && (
                          <div className="flex items-center gap-2 text-blue-700 bg-blue-50 px-2 py-1 rounded-lg w-fit mt-1">
                            <SafeIcon icon={FiNavigation} className="h-3 w-3" />
                            <span className="text-[11px] font-bold">
                              {getDistanceLabel(venue.distance, activePoi.name.toUpperCase())}
                            </span>
                          </div>
                        )}

                        {/* Afișare repere relevante pentru căutare (Ex: Arena Tenis) */}
                        {venue.nearby_landmarks?.map((l, i) => {
                           if (locationQuery && l.name.toLowerCase().includes(locationQuery.toLowerCase())) {
                              return (
                                <div key={i} className="flex items-center gap-2 text-green-700 bg-green-50 px-2 py-1 rounded-lg w-fit mt-1">
                                  <SafeIcon icon={FiNavigation} className="h-3 w-3" />
                                  <span className="text-[11px] font-bold">
                                    Lângă {l.name} ({l.distance})
                                  </span>
                                </div>
                              );
                           }
                           return null;
                        })}
                      </div>
                    </div>
                    
                    <div className="mt-4 flex flex-wrap gap-2">
                       {venue.facilities?.slice(0, 3).map((f, i) => (
                         <span key={i} className="text-[10px] bg-gray-100 px-2 py-1 rounded text-gray-600 font-medium">{f}</span>
                       ))}
                    </div>
                  </div>
                </motion.div>
              ))}
              
              {filteredVenues.length === 0 && (
                <div className="text-center py-12">
                  <SafeIcon icon={FiAlertTriangle} className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-900">Nu am găsit rezultate</h3>
                  <p className="text-gray-500">Încearcă să mărești raza de căutare sau să schimbi cuvintele cheie.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* MAP SECTION */}
        <div className="flex-1 min-h-[300px] lg:h-full relative order-1 lg:order-2 z-0">
          <MapContainer center={[searchCenter.lat, searchCenter.lng]} zoom={14} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <ChangeView center={searchCenter} zoom={14} />
            
            {/* Vizualizare Rază de Căutare */}
            <Circle 
              center={[searchCenter.lat, searchCenter.lng]} 
              radius={radius} 
              pathOptions={{ fillColor: '#3b82f6', fillOpacity: 0.1, color: '#3b82f6', weight: 1, dashArray: '5, 10' }}
            />

            {filteredVenues.map(venue => (
              venue.lat && venue.lng ? (
                <Marker 
                  key={venue.id} 
                  position={[venue.lat, venue.lng]} 
                  icon={createPriceIcon(venue.price_per_night)}
                  eventHandlers={{ click: () => navigate(`/book/${venue.owner_id}/${venue.id}`) }}
                />
              ) : null
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;